import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import {
  compactSelectClass,
  fieldClass,
  labelClass,
  primaryBtnClass,
  secondaryBtnClass,
} from '../styles';
import {
  fetchProductOrder,
  fetchProductOrders,
  setProductOrderStatus,
  updateProductOrder,
} from './api';
import MarketMultiSelect from './MarketMultiSelect';
import {
  OrdersMeta,
  ProductOrderFormValue,
  ProductOrderRow,
  formFromProductOrder,
} from './types';

interface Props {
  meta: OrdersMeta;
}

const ProductOrdersPanel: React.FC<Props> = ({ meta }) => {
  const [rows, setRows] = useState<ProductOrderRow[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ProductOrderRow | null>(null);
  const [form, setForm] = useState<ProductOrderFormValue | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [orgFilter, setOrgFilter] = useState<number | ''>('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchProductOrders({
        status: statusFilter || undefined,
        org: orgFilter || undefined,
        q: search.trim() || undefined,
      });
      setRows(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load product orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, orgFilter]);

  useEffect(() => {
    if (selectedId == null) {
      setDetail(null);
      setForm(null);
      return;
    }
    let cancelled = false;
    void fetchProductOrder(selectedId)
      .then((row) => {
        if (cancelled) return;
        setDetail(row);
        const next = formFromProductOrder(row);
        if (
          row.project?.lifecycle !== 'draft' &&
          next.markets.length === 0 &&
          row.project?.markets?.length === 1
        ) {
          next.markets = [row.project.markets[0]];
        }
        setForm(next);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load order');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const isDraftProject = detail?.project?.lifecycle === 'draft';

  const orderMarketOptions = useMemo(() => {
    if (isDraftProject) return form?.project_markets || [];
    return detail?.project?.markets || [];
  }, [isDraftProject, form?.project_markets, detail?.project?.markets]);

  const saveFields = async () => {
    if (!detail || !form) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const updated = await updateProductOrder(detail.id, form);
      setDetail(updated);
      setForm(formFromProductOrder(updated));
      setRows((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
      setMessage('Order saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const applyStatus = async () => {
    if (!detail || !form) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await updateProductOrder(detail.id, form);
      const updated = await setProductOrderStatus(detail.id, form.order_status, {
        opportunity_space: form.opportunity_space,
        markets: isDraftProject ? form.project_markets : undefined,
        gather_project_info: form.gather_project_info,
        territory_demand_space: form.territory_demand_space,
      });
      setDetail(updated);
      setForm(formFromProductOrder(updated));
      setRows((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
      setMessage(`Status set to ${updated.order_status}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      {(error || message) && (
        <div
          className={`rounded-md px-3 py-2 text-sm ${
            error ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-800'
          }`}
        >
          {error || message}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 min-h-[520px]">
        <div className="lg:col-span-4 border border-[#E8E8E8] rounded-lg overflow-hidden flex flex-col">
          <div className="px-3 py-2 border-b border-[#E8E8E8] space-y-2">
            <div className="flex items-center gap-2">
              <input
                className={`${fieldClass} h-9`}
                placeholder="Search orders…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void load();
                }}
              />
              <button type="button" className={secondaryBtnClass} onClick={() => void load()}>
                Search
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              <select
                className={compactSelectClass}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                aria-label="Filter by status"
              >
                <option value="">All statuses</option>
                {meta.product_order_statuses.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
              <select
                className={compactSelectClass}
                value={orgFilter}
                onChange={(e) => setOrgFilter(e.target.value ? Number(e.target.value) : '')}
                aria-label="Filter by organization"
              >
                <option value="">All orgs</option>
                {meta.orgs.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-[#8A8A8A] p-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : (
            <ul className="flex-1 max-h-[480px] overflow-y-auto">
              {rows.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedId(row.id);
                      setMessage(null);
                      setError(null);
                    }}
                    className={`w-full px-3 py-2.5 text-left text-sm border-b border-[#F0F0F0] ${
                      selectedId === row.id
                        ? 'bg-[#F3F8FC] text-[#0C0B0B] font-medium'
                        : 'text-[#4A4A4A] hover:bg-[#FAFAFA]'
                    }`}
                  >
                    <div className="truncate">{row.product_name || `Order #${row.id}`}</div>
                    <div className="text-[11px] text-[#8A8A8A] mt-0.5">
                      {row.user_display_name || row.user_email || '—'}
                      {` · ${row.credits} cr · ${row.order_status}`}
                    </div>
                  </button>
                </li>
              ))}
              {rows.length === 0 && (
                <li className="px-3 py-6 text-sm text-[#8A8A8A]">No product orders</li>
              )}
            </ul>
          )}
        </div>

        <div className="lg:col-span-8 border border-[#E8E8E8] rounded-lg p-4">
          {!detail || !form ? (
            <div className="h-full min-h-[280px] flex items-center justify-center text-sm text-[#8A8A8A] border border-dashed border-[#E0E0E0] rounded-lg">
              Select a product order to edit.
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <h3 className="text-sm font-bold text-[#0C0B0B]">
                    Order #{detail.id}
                    {detail.order_number ? ` · ${detail.order_number}` : ''}
                  </h3>
                  <p className="text-[12px] text-[#8A8A8A] mt-0.5">
                    {detail.user_display_name || detail.user_email}
                    {detail.org_name ? ` · ${detail.org_name}` : ''}
                    {detail.ledger
                      ? ` · Ledger ${detail.ledger.status} (${detail.ledger.amount})`
                      : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => void saveFields()}
                    className={`${secondaryBtnClass} inline-flex items-center gap-1.5`}
                  >
                    <Save className="w-3.5 h-3.5" />
                    Save fields
                  </button>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => void applyStatus()}
                    className={`${primaryBtnClass} inline-flex items-center gap-1.5`}
                  >
                    Apply status
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Order number</label>
                  <input
                    className={fieldClass}
                    value={form.order_number}
                    onChange={(e) => setForm({ ...form, order_number: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelClass}>Status</label>
                  <select
                    className={fieldClass}
                    value={form.order_status}
                    onChange={(e) => setForm({ ...form, order_status: e.target.value })}
                  >
                    {meta.product_order_statuses.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Product</label>
                  <input className={fieldClass} disabled value={detail.product_name || ''} />
                </div>
                <div>
                  <label className={labelClass}>Credits</label>
                  <input
                    type="number"
                    className={fieldClass}
                    value={form.credits}
                    onChange={(e) => setForm({ ...form, credits: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelClass}>Currency</label>
                  <input
                    className={fieldClass}
                    value={form.currency}
                    onChange={(e) => setForm({ ...form, currency: e.target.value })}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>Additional info</label>
                  <textarea
                    className={`${fieldClass} min-h-[64px]`}
                    value={form.additional_info}
                    onChange={(e) => setForm({ ...form, additional_info: e.target.value })}
                  />
                </div>
                <div className="sm:col-span-2">
                  <MarketMultiSelect
                    label="Order markets"
                    value={form.markets}
                    onChange={(markets) => setForm({ ...form, markets })}
                    allowedOptions={orderMarketOptions}
                    helper="Order markets must be a subset of the project markets."
                  />
                </div>
              </div>

              <div className="border-t border-[#E8E8E8] pt-4 space-y-3">
                <h4 className="text-sm font-bold text-[#0C0B0B]">Project</h4>
                {!detail.project ? (
                  <p className="text-sm text-[#8A8A8A]">No project linked to this order.</p>
                ) : isDraftProject ? (
                  <>
                    <div>
                      <label className={labelClass}>Name</label>
                      <input className={fieldClass} disabled value={detail.project.name} />
                    </div>
                    <div>
                      <label className={labelClass}>Opportunity space</label>
                      <textarea
                        className={`${fieldClass} min-h-[88px]`}
                        value={form.opportunity_space}
                        onChange={(e) =>
                          setForm({ ...form, opportunity_space: e.target.value })
                        }
                      />
                    </div>
                    <MarketMultiSelect
                      label="Project markets"
                      value={form.project_markets}
                      onChange={(project_markets) => {
                        const nextMarkets = form.markets.filter((m) =>
                          project_markets.includes(m)
                        );
                        if (project_markets.length === 1 && nextMarkets.length === 0) {
                          nextMarkets.push(project_markets[0]);
                        }
                        setForm({ ...form, project_markets, markets: nextMarkets });
                      }}
                      helper="Required before activating a draft project."
                    />
                    <label className="inline-flex items-center gap-2 text-sm text-[#4A4A4A]">
                      <input
                        type="checkbox"
                        checked={form.gather_project_info}
                        onChange={(e) =>
                          setForm({ ...form, gather_project_info: e.target.checked })
                        }
                      />
                      Run deep research after activation
                    </label>
                  </>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className={labelClass}>Name</div>
                      <div>{detail.project.name}</div>
                    </div>
                    <div>
                      <div className={labelClass}>Lifecycle</div>
                      <div className="capitalize">{detail.project.lifecycle}</div>
                    </div>
                    <div className="sm:col-span-2">
                      <div className={labelClass}>Opportunity space</div>
                      <div className="text-[#4A4A4A]">
                        {detail.project.opportunity_space || '—'}
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <div className={labelClass}>Markets</div>
                      <div>{detail.project.markets.join(', ') || '—'}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductOrdersPanel;
